import { useEffect, useRef } from "react";
import { useAppSelector } from "../../app/hooks";
import { selectAuth } from "../auth/authSlice";
import { apolloClient } from "../../apollo/graphql";
import { SESSION_HEARTBEAT } from "../../apollo/mutations";

// Send at most one heartbeat per interval while the user is interacting.
const HEARTBEAT_INTERVAL_MS = 60_000;

// Real user-interaction signals (not background network activity).
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];

/**
 * Keeps the server session "active" based on genuine user interaction. Unlike
 * the notification poller, this only fires when the human actually does
 * something, so an idle (but still-open) tab stops sending heartbeats and gets
 * revoked by the server's idle sweep.
 */
export function useSessionHeartbeat() {
  const { user } = useAppSelector(selectAuth);
  const isLoggedIn = !!(user && user.email);
  const lastSentRef = useRef(0);

  useEffect(() => {
    if (!isLoggedIn) return;

    const sendHeartbeat = () => {
      apolloClient
        .mutate({ mutation: SESSION_HEARTBEAT })
        .catch(() => undefined);
    };

    const onActivity = () => {
      const now = Date.now();
      if (now - lastSentRef.current < HEARTBEAT_INTERVAL_MS) return;
      lastSentRef.current = now;
      sendHeartbeat();
    };

    // Mark active right away on (re)login.
    lastSentRef.current = Date.now();
    sendHeartbeat();

    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, onActivity, { passive: true }),
    );
    return () => {
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, onActivity),
      );
    };
  }, [isLoggedIn, user?._id]);
}
